import { useCallback, useEffect, useState } from "react";
import api from "./Axios";

export const DEFAULT_CREATOR = "reya";
export const DEFAULT_USER_SORT = "signed_up_pacific";

const EMPTY = {
	summary: {},
	funnel: [],
	retention: [],
	signupTrend: [],
	modalityMix: [],
	mrrTrend: [],
	churnData: [],
	trialCapUsers: [],
	abandonCallRate: [],
	sessionDuration: { voice: [], video: [] },
	feedback: [],
	atRiskUsers: [],
	creators: [],
	creatorList: [],
	userStatsByKey: {},
	earnings: { creators: [], totals: {}, hold_days: 30 },
	softBanned: [],
	conversationUsersByCreator: {},
};

let cache = structuredClone(EMPTY);
let loaded = false;
let loading = false;
let inflight = null;
const listeners = new Set();

function snapshot() {
	return { cache, loaded, loading };
}

function notify() {
	const snap = snapshot();
	listeners.forEach((fn) => fn(snap));
}

export function subscribeAdminData(fn) {
	listeners.add(fn);
	return () => listeners.delete(fn);
}

export function getAdminDataSnapshot() {
	return snapshot();
}

async function getData(url) {
	try {
		const res = await api.get(url);
		return res?.data;
	} catch (err) {
		console.error("Admin data fetch failed:", url, err);
		return undefined;
	}
}

function formatFunnel(data) {
	if (!Array.isArray(data)) return [];
	return data.flatMap((item) =>
		Object.entries(item || {}).map(([key, value]) => ({ stage: key, value }))
	);
}

function formatSignup(data) {
	if (!Array.isArray(data)) return [];
	return data.map((item) => ({ day: item?.day, signups: item?.new_users }));
}

function formatAbandon(data) {
	if (!Array.isArray(data)) return [];
	return data.map((item) => ({ rate: item?.abandoned_pct, mode: item?.mode }));
}

function formatDuration(data) {
	if (!Array.isArray(data)) return [];
	return data.flatMap((item) =>
		Object.entries(item || {}).map(([key, value]) => ({ metric: key, minutes: value }))
	);
}

export function userStatsKey(creator, sortBy) {
	return `${creator && creator !== "all" ? creator : "all"}|${sortBy || DEFAULT_USER_SORT}`;
}

export function patchAdminData(updater) {
	cache = typeof updater === "function" ? updater(cache) : { ...cache, ...updater };
	notify();
}

export async function loadUserStats(creator = "all", sortBy = DEFAULT_USER_SORT, { force = false } = {}) {
	const key = userStatsKey(creator, sortBy);
	if (!force && cache.userStatsByKey[key]) return cache.userStatsByKey[key];
	const url = creator && creator !== "all"
		? `/admin/users/stats?creator_username=${creator}&sort_by=${sortBy}`
		: `/admin/users/stats?sort_by=${sortBy}`;
	const data = await getData(url);
	cache = {
		...cache,
		userStatsByKey: { ...cache.userStatsByKey, [key]: data || [] },
	};
	notify();
	return cache.userStatsByKey[key];
}

export async function loadConversationUsers(username, { force = false } = {}) {
	if (!username) return [];
	if (!force && cache.conversationUsersByCreator[username]) {
		return cache.conversationUsersByCreator[username];
	}
	const data = await getData(`/admin/users?username=${username}`);
	cache = {
		...cache,
		conversationUsersByCreator: {
			...cache.conversationUsersByCreator,
			[username]: data || [],
		},
	};
	notify();
	return cache.conversationUsersByCreator[username];
}

export async function loadAdminData({ force = false } = {}) {
	if (inflight) return inflight;
	if (loaded && !force) return cache;

	loading = true;
	notify();

	inflight = (async () => {
		try {
		const statsKey = userStatsKey("all", DEFAULT_USER_SORT);
		const extraStatKeys = force
			? Object.keys(cache.userStatsByKey).filter((key) => key !== statsKey)
			: [];
		const extraUserKeys = force
			? Object.keys(cache.conversationUsersByCreator).filter((username) => username !== DEFAULT_CREATOR)
			: [];

		const [
			summary,
			funnel,
			retention,
			signupTrend,
			modalityMix,
			mrrTrend,
			churnData,
			trialCapUsers,
			abandonCallRate,
			durationVoice,
			durationVideo,
			feedback,
			atRiskUsers,
			creators,
			creatorList,
			userStats,
			earnings,
			softBanned,
			reyaUsers,
			extraStatValues,
			extraUserValues,
		] = await Promise.all([
			getData("/admin/metrics/summary"),
			getData("/admin/metrics/funnel"),
			getData("/admin/metrics/retention"),
			getData("/admin/metrics/engagement/daily-series"),
			getData("/admin/metrics/engagement/modality-mix"),
			getData("/admin/metrics/monetization/mrr"),
			getData("/admin/metrics/monetization/churn"),
			getData("/admin/metrics/monetization/cap-hitters"),
			getData("/admin/metrics/reliability/abandoned-rate"),
			getData("/admin/metrics/reliability/duration?mode=voice"),
			getData("/admin/metrics/reliability/duration?mode=video"),
			getData("/admin/metrics/reliability/feedback?limit=5"),
			getData("/admin/metrics/engagement/at-risk"),
			getData("/admin/creators"),
			getData("/admin/creators/list"),
			getData(`/admin/users/stats?sort_by=${DEFAULT_USER_SORT}`),
			getData("/admin/creators/earnings"),
			getData("/admin/moderation/soft-banned"),
			getData(`/admin/users?username=${DEFAULT_CREATOR}`),
			Promise.all(extraStatKeys.map((key) => {
				const [creator, sortBy] = key.split("|");
				const url = creator && creator !== "all"
					? `/admin/users/stats?creator_username=${creator}&sort_by=${sortBy}`
					: `/admin/users/stats?sort_by=${sortBy}`;
				return getData(url);
			})),
			Promise.all(extraUserKeys.map((username) => getData(`/admin/users?username=${username}`))),
		]);

		const extraStatEntries = extraStatKeys.map((key, i) => [
			key,
			extraStatValues[i] || cache.userStatsByKey[key] || [],
		]);
		const extraUserEntries = extraUserKeys.map((username, i) => [
			username,
			extraUserValues[i] || cache.conversationUsersByCreator[username] || [],
		]);

		cache = {
			...cache,
			summary: summary || cache.summary,
			funnel: funnel ? formatFunnel(funnel) : cache.funnel,
			retention: retention || cache.retention,
			signupTrend: signupTrend ? formatSignup(signupTrend) : cache.signupTrend,
			modalityMix: modalityMix || cache.modalityMix,
			mrrTrend: mrrTrend || cache.mrrTrend,
			churnData: churnData || cache.churnData,
			trialCapUsers: trialCapUsers || cache.trialCapUsers,
			abandonCallRate: abandonCallRate ? formatAbandon(abandonCallRate) : cache.abandonCallRate,
			sessionDuration: {
				voice: durationVoice ? formatDuration(durationVoice) : cache.sessionDuration.voice,
				video: durationVideo ? formatDuration(durationVideo) : cache.sessionDuration.video,
			},
			feedback: feedback || cache.feedback,
			atRiskUsers: atRiskUsers || cache.atRiskUsers,
			creators: creators || cache.creators,
			creatorList: creatorList || cache.creatorList,
			userStatsByKey: {
				...Object.fromEntries(extraStatEntries),
				[statsKey]: userStats || cache.userStatsByKey[statsKey] || [],
			},
			earnings: earnings || cache.earnings,
			softBanned: softBanned?.users || cache.softBanned,
			conversationUsersByCreator: {
				...Object.fromEntries(extraUserEntries),
				[DEFAULT_CREATOR]: reyaUsers || cache.conversationUsersByCreator[DEFAULT_CREATOR] || [],
			},
		};

		loaded = true;
		return cache;
		} finally {
			loading = false;
			inflight = null;
			notify();
		}
	})();

	return inflight;
}

export function useAdminData() {
	const [state, setState] = useState(snapshot);

	useEffect(() => {
		setState(snapshot());
		const unsub = subscribeAdminData(setState);
		loadAdminData();
		return unsub;
	}, []);

	const refresh = useCallback(() => loadAdminData({ force: true }), []);

	return {
		...state.cache,
		loaded: state.loaded,
		loading: state.loading,
		refreshing: state.loading && state.loaded,
		refresh,
	};
}
